import { Logo } from "@/components/common/Logo"
import { Link } from "@tanstack/react-router"
import { Github, Globe, Linkedin, Twitter } from "lucide-react"

const PRODUCT_LINKS = [
  { label: "Payment Gateway", href: "/gateway" },
  { label: "Analytics", href: "/features" },
  { label: "API Documentation", href: "https://docs.orchestrate.global" },
  { label: "Webhooks", href: "https://docs.orchestrate.global/webhooks" },
  { label: "Sandbox", href: "https://docs.orchestrate.global/sandbox" },
]

const COMPANY_LINKS = [
  { label: "Features", href: "/features" },
  { label: "Partners", href: "/partners" },
  { label: "Legal", href: "/legal" },
  { label: "Compliance", href: "/compliance" },
  { label: "Blog", href: "/blog" },
]

const SOCIAL_LINKS = [
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Github, href: "#", label: "GitHub" },
]

export function Footer() {
  return (
    <footer className="border-border border-t bg-white" aria-label="Footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Main footer */}
        <div className="grid grid-cols-2 gap-10 py-16 lg:grid-cols-4">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <Logo className="mb-4" />
            <p className="text-muted mb-5 text-sm leading-relaxed">
              Unify your providers. Improve approvals. Scale with confidence.
            </p>
            <div className="flex gap-2.5">
              {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="bg-surface border-border hover:bg-blue-light hover:border-blue-mid flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-150"
                >
                  <Icon className="text-muted h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-muted mb-5 text-xs font-semibold tracking-widest uppercase">
              Product
            </h3>
            <ul className="space-y-3">
              {PRODUCT_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="text-muted hover:text-ink text-sm transition-colors duration-150"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-muted mb-5 text-xs font-semibold tracking-widest uppercase">
              Company
            </h3>
            <ul className="space-y-3">
              {COMPANY_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="text-muted hover:text-ink text-sm transition-colors duration-150"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-muted mb-5 text-xs font-semibold tracking-widest uppercase">
              Get in touch
            </h3>
            <div className="space-y-4">
              {[
                { type: "Sales", email: "business@orchestrate.global" },
                { type: "Support", email: "support@orchestrate.global" },
                { type: "General", email: "engage@orchestrate.global" },
              ].map(({ type, email }) => (
                <div key={type}>
                  <p className="text-muted/60 mb-1 text-xs font-semibold tracking-wider uppercase">
                    {type}
                  </p>
                  <a
                    href={`mailto:${email}`}
                    className="text-muted hover:text-blue text-sm transition-colors duration-150"
                  >
                    {email}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-border flex flex-col items-center justify-between gap-4 border-t py-6 sm:flex-row">
          <p className="text-muted text-xs">
            © {new Date().getFullYear()} Orchestrate, Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {["Privacy Policy", "Terms of Service", "Security"].map((item) => (
              <Link
                key={item}
                to="/legal"
                className="text-muted hover:text-ink text-xs transition-colors"
              >
                {item}
              </Link>
            ))}
            <button className="text-muted hover:text-ink border-border hover:border-blue-mid flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors">
              <Globe className="h-3.5 w-3.5" />
              English
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
